'use client';

import React, { useCallback, useEffect, useState, useTransition, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { MapPin, Clock, Briefcase, Heart, Search, SlidersHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './jobs.module.scss';
import { editorJsToPlainText } from '@/lib/editorjs-utils';
import { useTranslation } from '@/hooks/useTranslation';

// ── Types ──────────────────────────────────────────────────────
interface Recruiter { companyName: string; logoUrl?: string; isVerified: boolean; city?: string; country?: string; }
interface Industry  { id: string; name: string; }
interface Job {
  id: string;
  title: string;
  description: string;
  jobType: string;
  country?: string;
  city?: string;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency: string;
  publishedAt?: string;
  validTill?: string;
  applicationCount: number;
  recruiter: Recruiter;
  industry?: Industry;
}

interface JobsResponse {
  jobs: Job[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const JOB_TYPE_KEYS: Record<string, string> = {
  FULL_TIME: 'jobs.types.FULL_TIME',
  PART_TIME: 'jobs.types.PART_TIME',
  FREELANCE: 'jobs.types.FREELANCE',
  CONTRACT: 'jobs.types.CONTRACT',
};

function formatSalary(min?: number | string | null, max?: number | string | null, currency = 'USD') {
  const nMin = min !== undefined && min !== null ? Number(min) : undefined;
  const nMax = max !== undefined && max !== null ? Number(max) : undefined;
  if (!nMin && !nMax) return null;
  // pt-AO: . for thousands, , for decimals. Normalize non-breaking spaces that pt-AO outputs.
  const fmt = (n: number) => n.toLocaleString('pt-AO').replace(/[\u00a0\s]/g, '.');
  if (nMin && nMax) return `${currency} ${fmt(nMin)} – ${fmt(nMax)}`;
  if (nMax) return `Até ${currency} ${fmt(nMax)}`;
  return `A partir de ${currency} ${fmt(nMin!)}`;
}

function timeAgo(date?: string) {
  if (!date) return '';
  const diff = Date.now() - new Date(date).getTime();
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (h < 1) return 'Há pouco';
  if (h < 24) return `Há ${h}h`;
  if (d < 7) return `Há ${d} dia${d !== 1 ? 's' : ''}`;
  return new Date(date).toLocaleDateString('pt-PT');
}

// ── Jobs Page Content ─────────────────────────────────────────
function JobsContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useTranslation();

  const { data: session } = useSession();
  const isFreelancer = session?.user?.role === 'FREELANCER';

  const [jobs, setJobs] = useState<Job[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  // Map of jobId → savedJobId (string) for saved jobs
  const [savedMap, setSavedMap] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  // Filter state (controlled, synced to URL params)
  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const [country, setCountry] = useState(searchParams.get('country') ?? '');
  const [jobType, setJobType] = useState(searchParams.get('jobType') ?? '');
  const [salaryMin, setSalaryMin] = useState(searchParams.get('salaryMin') ?? '');
  const [salaryMax, setSalaryMax] = useState(searchParams.get('salaryMax') ?? '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') ?? 'date');
  const [page, setPage] = useState(Number(searchParams.get('page') ?? 1));

  const fetchJobs = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q)          params.set('q', q);
    if (country)    params.set('country', country);
    if (jobType)    params.set('jobType', jobType);
    if (salaryMin)  params.set('salaryMin', salaryMin);
    if (salaryMax)  params.set('salaryMax', salaryMax);
    params.set('sortBy', sortBy);
    params.set('page', String(page));
    params.set('limit', '20');

    // Sync to URL (without re-mounting)
    router.replace(`/jobs?${params.toString()}`, { scroll: false });

    fetch(`/api/v1/jobs?${params.toString()}`)
      .then((r) => r.json())
      .then((json: { success: boolean; data: JobsResponse }) => {
        if (json.success) {
          setJobs(json.data.jobs);
          setTotal(json.data.total);
          setTotalPages(json.data.totalPages);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [q, country, jobType, salaryMin, salaryMax, sortBy, page]); // eslint-disable-line

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Load saved job IDs for the current freelancer
  useEffect(() => {
    if (!isFreelancer) return;
    fetch('/api/v1/jobs/saved')
      .then((r) => r.json())
      .then((j) => {
        if (j.success) {
          const map: Record<string, string> = {};
          for (const s of (j.data.savedJobs ?? [])) map[s.job.id] = s.id;
          setSavedMap(map);
        }
      })
      .catch(() => { /* ignore */ });
  }, [isFreelancer]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchJobs();
  };

  const resetFilters = () => {
    setQ(''); setCountry(''); setJobType('');
    setSalaryMin(''); setSalaryMax('');
    setSortBy('date'); setPage(1);
  };

  const toggleSave = async (jobId: string) => {
    if (!isFreelancer || savingId) return;
    setSavingId(jobId);
    try {
      const r = await fetch('/api/v1/jobs/saved', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId }),
      });
      const j = await r.json();
      if (j.success) {
        setSavedMap((prev) => {
          const next = { ...prev };
          if (j.data.saved) {
            next[jobId] = j.data.savedJobId;
          } else {
            delete next[jobId];
          }
          return next;
        });
      }
    } catch { /* ignore */ }
    finally { setSavingId(null); }
  };

  return (
    <div className={styles.pageWrapper}>
      <Header />
      <main className={styles.main}>
        <aside className={styles.sidebar}>
          <div className={styles.filterCard}>
            <div className={styles.filterHeading}>
              <SlidersHorizontal className="w-4 h-4" /> {t('jobs.search.filters')}
            </div>

            <div className={styles.filterSection}>
              <h3 className={styles.filterLabel}>{t('jobs.filters.location')}</h3>
              <input
                className={styles.filterInput}
                type="text"
                placeholder={t('jobs.filters.locationPlaceholder')}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>

            <div className={styles.filterSection}>
              <h3 className={styles.filterLabel}>{t('jobs.filters.jobType')}</h3>
              <div className={styles.checkboxList}>
                {Object.entries(JOB_TYPE_KEYS).map(([val, key]) => (
                  <label key={val} className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={jobType === val}
                      onChange={() => setJobType(jobType === val ? '' : val)}
                      className={styles.checkbox}
                    />
                    {t(key)}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.filterSection}>
              <h3 className={styles.filterLabel}>{t('jobs.filters.salaryLabel')}</h3>
              <div className={styles.salaryRow}>
                <input className={styles.filterInput} type="number" placeholder={t('jobs.filters.salaryMin')} value={salaryMin} onChange={(e) => setSalaryMin(e.target.value)} />
                <input className={styles.filterInput} type="number" placeholder={t('jobs.filters.salaryMax')} value={salaryMax} onChange={(e) => setSalaryMax(e.target.value)} />
              </div>
            </div>

            <button className={styles.resetBtn} onClick={resetFilters}>{t('jobs.search.clearFilters')}</button>
          </div>
        </aside>

        <div className={styles.content}>
          {/* Search bar */}
          <form className={styles.searchBar} onSubmit={handleSearch}>
            <Search className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              type="text"
              placeholder={t('jobs.search.placeholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button type="submit" className={styles.searchBtn} id="btn-job-search">
              {t('jobs.search.searchButton')}
            </button>
          </form>

          {/* Sort + count */}
          <div className={styles.toolbar}>
            <span className={styles.resultsCount}>
              {loading ? '...' : t('jobs.search.resultsFound').replace('{{count}}', String(total))}
            </span>
            <div className={styles.sortBtns}>
              <button className={`${styles.sortBtn} ${sortBy === 'date' ? styles.sortActive : ''}`} onClick={() => { setSortBy('date'); setPage(1); }}>
                {t('jobs.search.sortByDate')}
              </button>
              <button className={`${styles.sortBtn} ${sortBy === 'salary' ? styles.sortActive : ''}`} onClick={() => { setSortBy('salary'); setPage(1); }}>
                {t('jobs.search.sortBySalary')}
              </button>
            </div>
          </div>

          {/* Job cards */}
          {loading ? (
            <div className={styles.jobList}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={`${styles.jobSkeleton} animate-pulse`} />
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className={styles.emptyState}>
              <Briefcase className="w-12 h-12 text-gray-300 mb-3" />
              <p className={styles.emptyTitle}>{t('jobs.search.noJobsFound')}</p>
              <p className={styles.emptySub}>{t('jobs.search.noJobsFound')}</p>
              <button className={styles.resetBtn} onClick={resetFilters}>{t('jobs.search.clearFilters')}</button>
            </div>
          ) : (
            <div className={styles.jobList}>
              {jobs.map((job) => {
                const salary = formatSalary(job.salaryMin, job.salaryMax, job.salaryCurrency);
                const isSaved = job.id in savedMap;
                return (
                  <article key={job.id} className={styles.jobCard}>
                    {/* Company logo */}
                    <div className={styles.jobLogoWrap}>
                      {job.recruiter.logoUrl ? (
                        <img src={job.recruiter.logoUrl} alt={job.recruiter.companyName} className={styles.jobLogo} />
                      ) : (
                        <div className={styles.jobLogoFallback}>
                          {job.recruiter.companyName.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    <div className={styles.jobBody}>
                      <div className={styles.jobTopRow}>
                        <div>
                          <h2 className={styles.jobTitle}>
                            <Link href={`/jobs/${job.id}`}>{job.title}</Link>
                          </h2>
                          <div className={styles.jobCompany}>
                            {job.recruiter.companyName}
                            {job.recruiter.isVerified && <span className={styles.verifiedBadge}>✓ {t('jobs.card.verified')}</span>}
                          </div>
                        </div>
                        {isFreelancer && (
                          <button
                            className={`${styles.saveBtn} ${isSaved ? styles.saveBtnActive : ''}`}
                            onClick={() => void toggleSave(job.id)}
                            disabled={savingId === job.id}
                          aria-label={isSaved ? t('jobs.card.unsave') : t('jobs.card.save')}
                            id={`btn-save-${job.id}`}
                          >
                            <Heart className="w-5 h-5" fill={isSaved ? 'currentColor' : 'none'} />
                          </button>
                        )}
                      </div>

                      <div className={styles.jobMeta}>
                        {(job.city || job.country) && (
                          <span><MapPin className="w-3.5 h-3.5" />{[job.city, job.country].filter(Boolean).join(', ')}</span>
                        )}
                        <span><Clock className="w-3.5 h-3.5" />{timeAgo(job.publishedAt)}</span>
                        <span><Briefcase className="w-3.5 h-3.5" />{t(`jobs.types.${job.jobType}`) || job.jobType}</span>
                        <span>{job.applicationCount} {job.applicationCount !== 1 ? t('jobs.card.applications') : t('jobs.card.application')}</span>
                      </div>

                      <p className={styles.jobDesc}>{editorJsToPlainText(job.description, 180)}</p>

                      <div className={styles.jobFooter}>
                        {salary && <div className={styles.jobSalary}>{salary}</div>}
                        {job.industry && <span className={styles.industryTag}>{job.industry.name}</span>}
                        <Link href={`/jobs/${job.id}`} className={styles.applyBtn} id={`btn-apply-${job.id}`}>
                          {t('jobs.card.apply')}
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                const p = i + 1;
                return (
                  <button
                    key={p}
                    className={`${styles.pageBtn} ${page === p ? styles.pageBtnActive : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className={styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function JobsPage() {
  return (
    <Suspense fallback={null}>
      <JobsContent />
    </Suspense>
  );
}
